import { useCallback, useEffect, useRef, useState } from 'react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl?: string;
}

export default function ShareModal({
  isOpen,
  onClose,
  shareUrl,
}: ShareModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const closeTimerRef = useRef<number | null>(null);
  const copiedTimerRef = useRef<number | null>(null);
  const errorTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    closeTimerRef.current = null;
    copiedTimerRef.current = null;
    errorTimerRef.current = null;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setManualUrl(shareUrl ?? window.location.href);
      setIsCopied(false);
      setCopyError(false);
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      clearTimers();
    };
  }, [isOpen, shareUrl, clearTimers]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => onClose(), 200);
  }, [onClose]);

  const copyWithExecCommand = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  };

  const handleCopyLink = async () => {
    if (!manualUrl) return;
    try {
      let copied = false;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(manualUrl);
        copied = true;
      } else {
        copied = copyWithExecCommand(manualUrl);
      }
      if (!copied) throw new Error('copy failed');
      setCopyError(false);
      setIsCopied(true);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(
        () => setIsCopied(false),
        1500
      );
    } catch {
      const copied = copyWithExecCommand(manualUrl);
      if (copied) {
        setCopyError(false);
        setIsCopied(true);
        if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = window.setTimeout(
          () => setIsCopied(false),
          1500
        );
        return;
      }
      setCopyError(true);
      if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = window.setTimeout(
        () => setCopyError(false),
        2200
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-end justify-center transition-colors duration-200 sm:items-center ${
        isVisible ? 'bg-[#0B1220]/60' : 'bg-[#0B1220]/0'
      }`}
      onClick={handleClose}
    >
      <div
        className={`max-h-[calc(100dvh-12px)] w-full max-w-[520px] overflow-y-auto rounded-t-3xl border border-black/[0.04] bg-white px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-4 shadow-[0_20px_56px_rgba(7,12,24,0.18)] transition-all duration-200 sm:mx-4 sm:max-h-[min(560px,calc(100dvh-32px))] sm:rounded-3xl sm:px-6 sm:pb-6 sm:pt-5 ${
          isVisible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-8 opacity-0 sm:translate-y-4'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-gray-10 sm:mb-5 sm:hidden" />

        <div className="mb-3 flex items-center justify-between sm:mb-4">
          <p className="text-[22px] font-bold leading-[1.4] tracking-[-0.02em] text-gray-100 sm:text-[24px]">
            공유하기
          </p>
          <button
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-gray-50 transition-colors hover:bg-gray-5 active:bg-gray-10 sm:h-10 sm:w-10"
            onClick={handleClose}
            aria-label="Close share modal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="rounded-2xl border border-black/[0.12] bg-gray-0">
          <div className="flex items-center">
            <input
              type="text"
              value={manualUrl}
              readOnly
              onFocus={e => e.currentTarget.select()}
              className="h-12 flex-1 rounded-l-2xl bg-transparent px-4 text-[14px] text-blue-55 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-50 sm:h-14 sm:text-[15px]"
            />
            <button
              className={`h-12 w-[88px] shrink-0 rounded-r-2xl border-l border-black/[0.08] text-[14px] font-bold transition-colors active:bg-gray-10 sm:h-14 sm:w-24 sm:text-[15px] ${
                isCopied
                  ? 'text-blue-50'
                  : copyError
                    ? 'text-red-500'
                    : 'text-gray-80'
              }`}
              onClick={handleCopyLink}
            >
              {isCopied ? '복사됨 ✓' : copyError ? '실패' : '복사'}
            </button>
          </div>
        </div>

        <div className="h-8 pt-2">
          <p
            className={`text-[12px] font-normal leading-[1.5] transition-opacity duration-150 sm:text-[13px] ${
              isCopied || copyError ? 'opacity-100' : 'opacity-0'
            } ${copyError ? 'text-red-500' : 'text-gray-50'}`}
          >
            {isCopied
              ? '링크가 복사됐어요 ✅'
              : copyError
                ? '복사에 실패했어요. 위 링크를 직접 복사해 주세요.'
                : ' '}
          </p>
        </div>
      </div>
    </div>
  );
}
